import { useState } from "react";
import { Plus, Trash, CaretRight, CaretDown, ArrowCounterClockwise, CalendarBlank } from "@phosphor-icons/react";

/* Editable phase-level + task-level project plan with actual dates.
 *
 * Props:
 *  - phases: [{ id, phase, start, days, driver?, status?, tasks?, overridden?, custom? }]
 *  - totalDays: number (max day axis)
 *  - startDate: ISO "YYYY-MM-DD" project start date
 *  - onStartDateChange(iso)
 *  - onPhaseChange(phaseId, patch): override an engine-seeded phase
 *  - onPhaseExtraChange(phaseId, patch | null=delete): manage custom phases
 *  - onAddPhase()
 *  - onReset()
 *  - hasOverrides: bool
 */

const STATUS_OPTIONS = [
    { v: "not_started", label: "Not Started" },
    { v: "in_progress", label: "In Progress" },
    { v: "complete", label: "Complete" },
];
const STATUS_TONE = {
    not_started: "bg-slate-100 text-slate-600 border-slate-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    complete: "bg-green-50 text-green-700 border-green-200",
};

function addDays(iso, n) {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + (Number(n) || 0));
    return d;
}
function fmt(d) {
    if (!d || isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short" });
}

export default function GanttTimeline({
    phases,
    totalDays,
    startDate,
    onStartDateChange,
    onPhaseChange,
    onPhaseExtraChange,
    onAddPhase,
    onReset,
    hasOverrides,
}) {
    const [expanded, setExpanded] = useState({});
    const maxDay = Math.max(
        totalDays || 1,
        ...phases.map((p) => (Number(p.start) || 1) + (Number(p.days) || 1) - 1),
    );
    const dayCols = Math.max(7, maxDay);
    const hasStart = !!startDate;
    const finishDate = hasStart ? addDays(startDate, maxDay - 1) : null;

    return (
        <div data-testid="gantt-timeline">
            {/* Project dates header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3 p-3 rounded-[6px] bg-[var(--rom-navy)]/[0.04] border border-[var(--rom-border)]">
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-[11px] uppercase tracking-[0.12em] font-bold text-[var(--rom-text)] flex items-center gap-1.5">
                        <CalendarBlank size={14} weight="bold" className="text-[var(--rom-navy)]" /> Project start
                        <span className="text-[var(--rom-danger)]">*</span>
                    </label>
                    <input
                        type="date"
                        className="input font-mono text-sm py-1.5 w-[170px]"
                        value={startDate || ""}
                        onChange={(e) => onStartDateChange(e.target.value)}
                        data-testid="project-start-date"
                    />
                    <div className="text-sm text-[var(--rom-text-2)]">
                        Finish: <b className="text-[var(--rom-text)] font-mono" data-testid="project-finish-date">{hasStart ? fmt(finishDate) : "set a start date"}</b>
                        {hasStart && <span className="text-[var(--rom-text-3)]"> · {maxDay} day{maxDay > 1 ? "s" : ""}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasOverrides && (
                        <button onClick={onReset} className="btn btn-ghost btn-sm" data-testid="gantt-reset">
                            <ArrowCounterClockwise size={12} weight="bold" /> Reset to suggested
                        </button>
                    )}
                    <button onClick={onAddPhase} className="btn btn-ghost btn-sm" data-testid="gantt-add-phase">
                        <Plus size={12} weight="bold" /> Add phase
                    </button>
                </div>
            </div>

            {!hasStart && (
                <div className="text-[11px] mb-3 text-[var(--rom-orange-dark)] bg-[var(--rom-orange-soft)]/60 border border-[var(--rom-orange)]/40 rounded px-2 py-1.5" data-testid="start-date-required">
                    Pick a Project Start Date to generate actual phase dates. Phases below show relative days until you do.
                </div>
            )}

            <div className="text-xs text-[var(--rom-text-2)] mb-2">
                Suggested phases are generated from your ROM criteria. Edit names, dates, durations and status — or add / remove phases. Click a row to add tasks.
            </div>

            {/* Rows */}
            <div className="space-y-1.5">
                {phases.map((p) => (
                    <PhaseRow
                        key={p.id}
                        phase={p}
                        dayCols={dayCols}
                        startDate={startDate}
                        expanded={!!expanded[p.id]}
                        onToggle={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                        onChange={(patch) => p.custom ? onPhaseExtraChange(p.id, patch) : onPhaseChange(p.id, patch)}
                        onDelete={p.custom ? () => onPhaseExtraChange(p.id, null) : null}
                    />
                ))}
                {phases.length === 0 && (
                    <div className="text-center py-6 text-[var(--rom-text-3)] text-sm border border-dashed border-[var(--rom-border-strong)] rounded">
                        No phases yet. Click <b>+ Add phase</b> to build the schedule.
                    </div>
                )}
            </div>

            <div className="mt-3 text-xs text-[var(--rom-text-2)]">
                Total schedule: <b>{maxDay} day{maxDay > 1 ? "s" : ""}</b> · {phases.length} phase{phases.length === 1 ? "" : "s"}
                {hasStart && <> · {fmt(addDays(startDate, 0))} → {fmt(finishDate)}</>}
            </div>
        </div>
    );
}

function PhaseRow({ phase, dayCols, startDate, expanded, onToggle, onChange, onDelete }) {
    const start = Math.max(1, Number(phase.start) || 1);
    const days = Math.max(1, Number(phase.days) || 1);
    const leftPct = ((start - 1) / dayCols) * 100;
    const widthPct = (days / dayCols) * 100;
    const status = phase.status || "not_started";
    const startD = startDate ? addDays(startDate, start - 1) : null;
    const finishD = startDate ? addDays(startDate, start + days - 2) : null;

    const setTask = (idx, key, value) => {
        const tasks = [...(phase.tasks || [])];
        tasks[idx] = { ...tasks[idx], [key]: key === "label" ? value : Number(value) || 0 };
        onChange({ tasks });
    };
    const addTask = () => {
        const tasks = [...(phase.tasks || []), { id: `t-${Date.now()}`, label: "New task", start, days: 1 }];
        onChange({ tasks });
    };
    const delTask = (idx) => {
        const tasks = (phase.tasks || []).filter((_, i) => i !== idx);
        onChange({ tasks });
    };

    return (
        <div className="border border-[var(--rom-border)] rounded-[4px] bg-white overflow-hidden" data-testid={`gantt-phase-${phase.id}`}>
            <div className="flex flex-col gap-2 p-2 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex items-center gap-2 min-w-0 sm:contents">
                    <button
                        onClick={onToggle}
                        className="p-0.5 hover:bg-slate-100 rounded shrink-0"
                        data-testid={`gantt-toggle-${phase.id}`}
                    >
                        {expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                    </button>
                    <div className="flex-1 min-w-0 sm:flex-none sm:shrink-0 sm:w-[230px]">
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                className="flex-1 min-w-0 bg-transparent border border-transparent hover:border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none rounded px-2 py-1 text-sm font-medium"
                                value={phase.phase || ""}
                                onChange={(e) => onChange({ phase: e.target.value })}
                                data-testid={`gantt-phase-name-${phase.id}`}
                            />
                            {phase.overridden && !phase.custom && <span className="chip chip-orange shrink-0" style={{ padding: "1px 5px" }}>edited</span>}
                            {phase.custom && <span className="chip chip-green shrink-0" style={{ padding: "1px 5px" }}>custom</span>}
                        </div>
                        {phase.driver && (
                            <div className="text-[10px] text-[var(--rom-text-3)] px-2 mt-0.5 leading-tight" data-testid={`gantt-phase-driver-${phase.id}`}>{phase.driver}</div>
                        )}
                    </div>
                </div>

                <div className="relative h-7 w-full sm:flex-1 sm:w-auto bg-slate-50 rounded-sm border border-slate-200 overflow-hidden">
                    <div
                        className="absolute top-0 bottom-0 bg-[var(--rom-navy)] flex items-center pl-2 text-white text-[11px] font-semibold rounded-sm"
                        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                        title={`Day ${start} → ${start + days - 1}`}
                    >
                        {days}d
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:contents">
                    <div className="w-auto sm:w-[150px] sm:shrink-0 text-[10px] font-mono text-[var(--rom-text-2)] leading-tight" data-testid={`gantt-dates-${phase.id}`}>
                        {startDate ? (
                            <>
                                <div>{fmt(startD)}</div>
                                <div className="text-[var(--rom-text-3)]">→ {fmt(finishD)}</div>
                            </>
                        ) : (
                            <div className="text-[var(--rom-text-3)]">Day {start}–{start + days - 1}</div>
                        )}
                    </div>

                    <div className="w-auto sm:w-[110px] sm:shrink-0 flex items-center gap-1">
                        <NumInput label="Start" value={phase.start} onChange={(v) => onChange({ start: Number(v) || 1 })} testId={`gantt-start-${phase.id}`} />
                        <NumInput label="Days" value={phase.days} onChange={(v) => onChange({ days: Number(v) || 1 })} testId={`gantt-days-${phase.id}`} />
                    </div>

                    <select
                        className={`shrink-0 text-[11px] font-semibold rounded border px-1.5 py-1 focus:outline-none ${STATUS_TONE[status]}`}
                        value={status}
                        onChange={(e) => onChange({ status: e.target.value })}
                        data-testid={`gantt-status-${phase.id}`}
                    >
                        {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>

                    {onDelete && (
                        <button onClick={onDelete} className="text-[var(--rom-danger)] hover:opacity-70 p-1 shrink-0" data-testid={`gantt-del-${phase.id}`}>
                            <Trash size={14} />
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="border-t border-[var(--rom-border)] bg-slate-50/50 p-2 space-y-1">
                    {(phase.tasks || []).map((t, idx) => {
                        const tStart = Math.max(1, Number(t.start) || 1);
                        const tDays = Math.max(1, Number(t.days) || 1);
                        return (
                            <div key={t.id || idx} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3" data-testid={`gantt-task-${phase.id}-${idx}`}>
                                <div className="w-full sm:w-[230px] sm:pl-6 sm:shrink-0">
                                    <input
                                        className="w-full bg-white border border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none rounded px-2 py-1 text-xs"
                                        value={t.label || ""}
                                        onChange={(e) => setTask(idx, "label", e.target.value)}
                                        placeholder="Task label"
                                        data-testid={`gantt-task-label-${phase.id}-${idx}`}
                                    />
                                </div>
                                <div className="relative h-5 w-full sm:flex-1 sm:w-auto bg-white rounded-sm border border-slate-200 overflow-hidden">
                                    <div
                                        className="absolute top-0 bottom-0 bg-[var(--rom-orange)] rounded-sm"
                                        style={{ left: `${((tStart - 1) / dayCols) * 100}%`, width: `${(tDays / dayCols) * 100}%` }}
                                    />
                                </div>
                                <div className="flex items-center gap-2 sm:contents">
                                    <div className="w-[110px] shrink-0 flex items-center gap-1">
                                        <NumInput value={t.start} onChange={(v) => setTask(idx, "start", v)} testId={`gantt-task-start-${phase.id}-${idx}`} />
                                        <NumInput value={t.days} onChange={(v) => setTask(idx, "days", v)} testId={`gantt-task-days-${phase.id}-${idx}`} />
                                    </div>
                                    <button onClick={() => delTask(idx)} className="text-[var(--rom-danger)] hover:opacity-70 p-1 shrink-0" data-testid={`gantt-task-del-${phase.id}-${idx}`}>
                                        <Trash size={12} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    <button onClick={addTask} className="w-full py-1.5 text-[11px] font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] hover:bg-white rounded transition-colors" data-testid={`gantt-add-task-${phase.id}`}>
                        + Add task
                    </button>
                </div>
            )}
        </div>
    );
}

function NumInput({ label, value, onChange, testId }) {
    return (
        <div className="flex-1">
            {label && <div className="text-[9px] uppercase tracking-wider text-[var(--rom-text-3)] leading-none">{label}</div>}
            <input
                type="number" min={1}
                className="w-full bg-white border border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none rounded px-1.5 py-0.5 text-xs font-mono text-right"
                value={value === undefined || value === null ? "" : value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            />
        </div>
    );
}
